import React, { useState } from 'react';
import {
  FiSearch,
  FiX,
  FiSliders,
  FiFilter,
  FiCreditCard,
  FiCalendar,
} from 'react-icons/fi';
import useWindowSize from 'shared/hooks/useWindowSize';

const ORDEN_OPCIONES = [
  { id: 'reciente', label: 'Más recientes' },
  { id: 'antiguo', label: 'Más antiguos' },
  { id: 'monto_mayor', label: 'Mayor monto' },
  { id: 'monto_menor', label: 'Menor monto' },
];

const FECHA_OPCIONES = [
  { id: '', label: 'Todo' },
  { id: 'hoy', label: 'Hoy' },
  { id: 'ayer', label: 'Ayer' },
  { id: '7dias', label: '7 días' },
  { id: '30dias', label: '30 días' },
  { id: 'personalizado', label: 'Personalizado' },
];

const chipStyle = (active, color) => ({
  padding: '7px 14px',
  borderRadius: 20,
  border: `1.5px solid ${active ? color : 'var(--agx-border)'}`,
  background: active ? color : 'var(--agx-surface)',
  color: active ? '#fff' : 'var(--agx-muted)',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: active ? 700 : 500,
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'all 0.15s',
});

const selectStyle = {
  appearance: 'none',
  padding: '9px 12px',
  borderRadius: 10,
  border: '1px solid var(--agx-border)',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  backgroundColor: 'var(--agx-surface)',
  color: 'var(--agx-text)',
  cursor: 'pointer',
  maxWidth: '180px',
};

const dateStyle = {
  padding: '7px 10px',
  borderRadius: 10,
  border: '1px solid var(--agx-border)',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  backgroundColor: 'var(--agx-bg)',
  color: 'var(--agx-text)',
};

/**
 * Filtros de historial compartidos (tienda y online).
 * `filtros` y `set` se pasan desde el padre; este componente es puramente visual.
 */
const HistorialFiltros = ({
  filtros,
  set,
  opciones,
  onLimpiar,
  hayActivos,
  modoModal = false,
}) => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [modalAbierto, setModalAbierto] = useState(false);

  const { estados = [], tipos = null, metodos = [] } = opciones;

  const usarModal = modoModal || isMobile;

  const numFiltrosAvanzados = [
    filtros.estado,
    filtros.tipo,
    filtros.fecha,
    filtros.desde,
    filtros.hasta,
    filtros.metodo,
    filtros.orden !== 'reciente' ? filtros.orden : '',
  ].filter(Boolean).length;

  const busquedaInput = (
    <div style={{ position: 'relative' }}>
      <FiSearch
        style={{ position: 'absolute', left: 12, top: 11, color: 'var(--agx-faint)', pointerEvents: 'none' }}
        size={16}
      />
      <input
        value={filtros.busqueda}
        onChange={(e) => set.busqueda(e.target.value)}
        placeholder="Buscar: cliente, teléfono, cédula, dirección, sede, método de pago, #id..."
        style={{
          width: '100%',
          padding: '10px 34px 10px 38px',
          borderRadius: 12,
          border: `1.5px solid ${filtros.busqueda ? 'var(--agx-pri2)' : 'var(--agx-border)'}`,
          fontSize: 13,
          outline: 'none',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
          backgroundColor: 'var(--agx-bg)',
          color: 'var(--agx-text)',
          transition: 'border-color 0.2s',
        }}
      />
      {filtros.busqueda && (
        <button
          onClick={() => set.busqueda('')}
          style={{
            position: 'absolute', right: 10, top: 10, border: 'none',
            background: 'var(--agx-border)', borderRadius: '50%', width: 20, height: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--agx-muted)',
          }}
        >
          <FiX size={12} />
        </button>
      )}
    </div>
  );

  const filtrosAvanzados = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Estado */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {estados.map((t) => (
          <button key={t.id} onClick={() => set.estado(t.id)} style={chipStyle(filtros.estado === t.id, 'var(--agx-pri2)')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tipo de entrega (solo online) */}
      {tipos && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {tipos.map((t) => (
            <button key={t.id} onClick={() => set.tipo(t.id)} style={chipStyle(filtros.tipo === t.id, 'var(--agx-pri2)')}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Fecha */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {FECHA_OPCIONES.map((t) => (
          <button key={t.id} onClick={() => set.fecha(t.id)} style={chipStyle(filtros.fecha === t.id, '#f59e0b')}>
            {t.label}
          </button>
        ))}
      </div>

      {filtros.fecha === 'personalizado' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <FiCalendar size={14} color="var(--agx-muted)" />
          <span style={{ fontSize: 11, color: 'var(--agx-muted)', fontWeight: 600 }}>Desde</span>
          <input type="date" value={filtros.desde} onChange={(e) => set.desde(e.target.value)} style={dateStyle} />
          <span style={{ fontSize: 11, color: 'var(--agx-muted)', fontWeight: 600 }}>Hasta</span>
          <input type="date" value={filtros.hasta} onChange={(e) => set.hasta(e.target.value)} style={dateStyle} />
        </div>
      )}

      {/* Método de pago */}
      {metodos.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiCreditCard size={14} color="var(--agx-muted)" />
          <select value={filtros.metodo} onChange={(e) => set.metodo(e.target.value)} style={selectStyle}>
            <option value="">Todos los métodos</option>
            {metodos.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      )}

      {/* Orden */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FiSliders size={14} color="var(--agx-muted)" />
        <select value={filtros.orden} onChange={(e) => set.orden(e.target.value)} style={selectStyle}>
          {ORDEN_OPCIONES.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </div>

      {hayActivos && (
        <button
          onClick={onLimpiar}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '9px 13px', borderRadius: 10,
            border: '1px solid var(--agx-border)', background: 'var(--agx-surface)',
            color: 'var(--agx-muted)', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}
        >
          <FiX size={13} /> Limpiar filtros
        </button>
      )}
    </div>
  );

  if (usarModal) {
    return (
      <>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>{busquedaInput}</div>
          <button
            onClick={() => setModalAbierto(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 14px', borderRadius: 12, flexShrink: 0,
              border: `1.5px solid ${numFiltrosAvanzados > 0 ? 'var(--agx-pri2)' : 'var(--agx-border)'}`,
              background: 'var(--agx-surface)', color: 'var(--agx-text)',
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <FiFilter size={15} /> Filtrar
            {numFiltrosAvanzados > 0 && (
              <span style={{ background: 'var(--agx-pri2)', color: '#fff', minWidth: 18, height: 18, borderRadius: 9, fontSize: 11, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>
                {numFiltrosAvanzados}
              </span>
            )}
          </button>
        </div>

        {modalAbierto && (
          <div
            onClick={() => setModalAbierto(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200,
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--agx-surface)', width: '100%', maxWidth: 600,
                maxHeight: '85vh', overflowY: 'auto',
                borderTopLeftRadius: 20, borderTopRightRadius: 20,
                padding: '16px 16px 24px',
                boxShadow: '0 -10px 40px rgba(0,0,0,0.3)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--agx-text)' }}>Filtros</span>
                <button onClick={() => setModalAbierto(false)} style={{ border: 'none', background: 'var(--agx-bg)', borderRadius: 8, cursor: 'pointer', padding: 8, color: 'var(--agx-muted)', display: 'flex' }}>
                  <FiX size={18} />
                </button>
              </div>
              {filtrosAvanzados}
              <button
                onClick={() => { setModalAbierto(false); }}
                style={{
                  width: '100%', marginTop: 16, padding: 13, borderRadius: 12,
                  border: 'none', background: 'var(--agx-pri2)', color: '#fff',
                  fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Aplicar filtros
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {busquedaInput}
      {filtrosAvanzados}
    </div>
  );
};

export default HistorialFiltros;
